import { FormSectionTitle } from 'atoms/FormSectionTitle';
import { TransitionButton } from 'atoms/TransitionButton';
import { InquirySchemaType } from 'commons/Inquiry';
import { InquiryTypes } from 'domains/InquiryType';
import { useTranslationWithLang } from 'hooks/useTranslationWithLang';
import { FieldErrorMessages } from 'molecules/FieldErrorMessages';
import { LabeledInquiryField } from 'molecules/LabeldInquiryField';
import { LabeledInputField } from 'molecules/LabeledInputField';
import { useForm } from 'react-hook-form';
import { AppFooter } from 'templates/AppFooter';
import { PageContent } from 'templates/PageContent';
import { Button } from 'atoms/Button';
import { zodResolver } from '@hookform/resolvers/zod';
import { useContext, useEffect, useState } from 'react';
import { useInquirySchema } from 'hooks/U012/useInquirySchema';
import { Form } from 'templates/Form';
import { AppHeader } from 'templates/AppHeader';
import { useGetInquiryInfo } from 'hooks/U012/useGetInquiryInfoHooks';
import { AppContext } from 'templates/AppContextProvider';
import LoadingModal from 'organisms/LoadingModal';
import MessageModal from 'organisms/MessageModal';
import { useNavigate } from 'react-router-dom';
import { PATHS } from 'commons/paths';

type InquiryFormProps = {
  onSubmit: (data: InquirySchemaType) => void;
};

export function InquiryForm({ onSubmit }: InquiryFormProps) {
  const { t: tInquiry } = useTranslationWithLang('inquiry');
  const InquirySchema = useInquirySchema();
  const ctx = useContext(AppContext);
  const userId = ctx.userId;
  const [data, loading, error] = useGetInquiryInfo(userId);
  const [errorMessage, setErrorMessage] = useState<string>('');
  const nav = useNavigate();

  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors },
    clearErrors,
  } = useForm<InquirySchemaType>({
    resolver: zodResolver(InquirySchema),
    shouldFocusError: true,
    defaultValues: {
      firstName: data?.firstname,
      lastName: data?.lastname,
      firstNameKana: data?.firstname_kana,
      lastNameKana: data?.lastname_kana,
      mail: data?.mail,
      phoneNumber: data?.phone1,
      inquiryType: '0',
      inquiry: '',
    },
  });
  useEffect(() => {
    clearErrors();
  }, [ctx.currentLang, clearErrors]);

  useEffect(() => {
    if (data) {
      setValue('firstName', data.firstname);
      setValue('lastName', data.lastname);
      setValue('firstNameKana', data.firstname_kana);
      setValue('lastNameKana', data.lastname_kana);
      setValue('mail', data.mail);
      setValue('phoneNumber', data.phone1);
    }
  }, [data]);
  useEffect(() => {
    if (error) {
      setErrorMessage(tInquiry('TU012_015'));
    }
  }, [error]);

  return (
    <>
      {loading && <LoadingModal />}
      {error && (
        <MessageModal
          message={errorMessage}
          closeLabel={tInquiry('TU012_011')}
          onClose={() => {
            nav(PATHS.MYPAGE);
          }}
        />
      )}
      <AppHeader />
      <div
        role="heading"
        aria-level={1}
        aria-live="assertive"
        id="inquiry-form-title"
        className="sr-only"
      >
        {tInquiry('TU012_001')}
      </div>
      <PageContent>
        <form onSubmit={handleSubmit(onSubmit)}>
          <Form title={tInquiry('TU012_001')}>
            <LabeledInputField
              title={tInquiry('TU012_005')}
              placeholder={'example@example.com'}
              className="w-[25rem]"
              ariaLabel={tInquiry('TU012_005')}
              autoComplete="email"
              id="mailAddress"
              required={true}
              {...register('mail')}
            />
            <FieldErrorMessages messages={[errors.mail?.message]} />
            <div className="flex flex-col w-full">
              <FormSectionTitle text={tInquiry('TU012_014')} htmlFor="inquiryType" />
              <select
                {...register('inquiryType')}
                id="inquiryType"
                aria-label={tInquiry('TU012_014')}
                className="text-[--c-textinput-text] bg-[--c-textinput-background] border-[--c-textinput-border] rounded-[8px] h-[40px] px-2 border-[1px]"
                tabIndex={0}
                aria-required="true"
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                  }
                }}
              >
                <option value="" disabled>
                  {tInquiry('TU012_014')}
                </option>
                {InquiryTypes.map((type) => (
                  <option key={type.value} value={type.value}>
                    {tInquiry(type.label)}
                  </option>
                ))}
              </select>
              <FieldErrorMessages messages={[errors.inquiryType?.message]} />
            </div>
            <LabeledInquiryField
              title={tInquiry('TU012_007')}
              placeholder={tInquiry('TU012_007')}
              ariaLabel={tInquiry('TU012_007')}
              id="inquiry"
              {...register('inquiry')}
            />
            <FieldErrorMessages messages={[errors.inquiry?.message]} />
            <div className={'flex justify-center items-center'}>
              <TransitionButton
                className="mr-6 w-[9rem]"
                style={{ paddingTop: '0.9rem', paddingBottom: '0.9rem' }}
              />
              <Button label={tInquiry('TU012_021')} type="submit" className="w-[9rem]" />
            </div>
          </Form>
        </form>
      </PageContent>
      <AppFooter />
    </>
  );
}
